import { CASES } from "./constants";
import { safeGetJSON } from "tap/utils/safeLocalStorage";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  getInitialPaginationRange,
  getStoredItemsPerPage,
  PAGINATION_STORAGE_KEYS,
  persistItemsPerPageFromPayload,
  withPreservedPagination,
} from "tap/utils/paginationPreferences.js";

const paginationKeys = {
  itemsPerPage: PAGINATION_STORAGE_KEYS.rateCards,
};
const initialItemsPerPage = getStoredItemsPerPage(paginationKeys.itemsPerPage);
const initialPaginationRange = getInitialPaginationRange(initialItemsPerPage);

/**
 * Retrieves persisted sort preference from localStorage.
 * @returns {Array} Sort tuple [field, direction], defaults to ['id', 'DESC']
 */
const getInitialRateCardsSort = () => {
  return safeGetJSON("rateCardsSort", ["id", "DESC"]);
};

/**
 * RateCards Redux State Shape
 * @typedef {Object} RateCardsState
 * @property {Array} rateCards - List of rate card records from API
 * @property {string} rateCard - Currently selected rate card ID
 * @property {boolean} isCreateDrawerOpen - Create drawer visibility
 * @property {boolean} isEditDrawerOpen - Edit drawer visibility
 * @property {boolean} isUploadTasksDrawerOpen - Upload tasks drawer visibility
 * @property {boolean} isViewRateCardDrawerOpen - View drawer visibility
 * @property {Object} selectedRateCard - Full rate card object for viewing/editing
 * @property {Array} rateCardJobTasks - Tasks associated with selected rate card
 * @property {Array} rateCardHistory - Audit history for selected rate card
 * @property {Object} filterForm - Search/filter form values
 * @property {Object} rateCardForm - Create/edit form values
 * @property {string} selectedHistory - Selected history entry ID
 * @property {Array} statusOptions - Rate card status options
 * @property {Array} revShareOptions - Revenue share type options (REV_SHARE, NON_REV_SHARE, etc.)
 * @property {Array} priceTypeOptions - Price type options (LICENSE_FEE, MIN_DEEMED_PRICE)
 * @property {Array} currencyOptions - Currency options from API
 * @property {Array} formatOptions - Format options from API
 * @property {Array} windowSequenceOptions - Window sequence options from API
 * @property {Array} userOptions - User options for filtering
 * @property {number} totalPages - Pagination total pages
 * @property {number} totalItems - Total record count
 * @property {Array} rateCardsRange - Pagination range [start, end]
 * @property {Array} rateCardsSort - Sort tuple [field, direction]
 * @property {boolean} refreshRateCards - Flag to trigger grid data refetch
 */
const initialState = {
  rateCards: [],
  rateCard: "",
  isCreateDrawerOpen: false,
  isEditDrawerOpen: false,
  isUploadTasksDrawerOpen: false,
  isViewRateCardDrawerOpen: false,
  selectedRateCard: {},
  rateCardJobTasks: [],
  rateCardHistory: [],
  filterForm: {
    rateCardId: "",
    rateCardNameKeywords: "",
    keywordMatchType: "all",
    tprDisplay: "hide",
    users: "",
    status: [
      {
        label: "Active",
        value: "ACTIVE",
      },
    ],
  },
  rateCardForm: {
    tierAliases: "",
    windows: "",
    status: "ACTIVE",
    isTpr: false,
    rateCardName: "",
    windowSequence: "",
    currency: "",
    priceType: "",
    revShareType: "",
    windowSequenceId: "",
  },
  selectedHistory: "",
  statusOptions: [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
    { value: "NEEDS_ATTENTION", label: "Needs Attention" },
  ],
  revShareOptions: [
    { label: "Rev Share", value: "REV_SHARE" },
    { label: "Rev Share %", value: "REV_SHARE_PERCENTAGE" },
    { label: "Rev Share %+", value: "REV_SHARE_PERCENTAGE_PLUS" },
    { label: "Non-Rev Share", value: "NON_REV_SHARE" },
    { label: "VOD PPV", value: "VOD_PPV" },
    { label: "VOD PPV %", value: "VOD_PPV_PERCENTAGE" },
    { label: "Hotel", value: "HOTEL" },
    { label: "Rev Share % B", value: "REV_SHARE_PERCENTAGE_B" },
    { label: "Rev Share +", value: "REV_SHARE_PLUS" },
  ],
  priceTypeOptions: [
    { label: "License Fee", value: "LICENSE_FEE" },
    { label: "Min Deemed Price", value: "MIN_DEEMED_PRICE" },
  ],
  currencyOptions: [],
  formatOptions: [],
  windowSequenceOptions: [],
  userOptions: [],
  totalPages: 0,
  totalItems: 0,
  currentPage: 0,
  filterParam: "",
  rateCardsRange: initialPaginationRange,
  rateCardsSort: getInitialRateCardsSort(),
  defaultFilterRange: initialPaginationRange,
  itemsPerPage: initialItemsPerPage,
  pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
  refreshRateCards: false,
};

export function rateCards(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case CASES.UPDATE_RATE_CARDS: {
      // Persist sort preference to localStorage for session continuity
      if (payload?.rateCardsSort) {
        localStorage.setItem(
          "rateCardsSort",
          JSON.stringify(payload.rateCardsSort),
        );
      }
      persistItemsPerPageFromPayload(payload, paginationKeys);
      return { ...state, ...payload };
    }
    case CASES.UPDATE_RATE_CARDS_SEARCH_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.UPDATE_RATE_CARD_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.CLEAR_RATE_CARDS: {
      return withPreservedPagination(
        state,
        {
          ...initialState,
          rateCardsSort: state.rateCardsSort,
          userOptions: state.userOptions,
        },
        ["rateCardsRange"],
      );
    }
    case CASES.CLEAR_RATE_CARD_FORM: {
      // Only clear drawer/form-related state, preserve grid data and filters
      return {
        ...state,
        rateCardForm: initialState.rateCardForm,
        selectedRateCard: initialState.selectedRateCard,
        rateCardHistory: initialState.rateCardHistory,
        selectedHistory: initialState.selectedHistory,
        rateCardJobTasks: initialState.rateCardJobTasks,
      };
    }
    default:
      return state;
  }
}
